import { useState } from 'react';

import fidelityLogo from '@/assets/images/fidelity-logo.svg';
import secclLogo from '@/assets/images/seccl-logo.png';
import avivaLogo from '@/assets/images/aviva-logo.svg';
import MiaSignature from '@/components/MiaSignature';
import InfoSheet from '@/components/InfoSheet';
import { cn } from '@/lib/utils';
import { MINIMUM_AMOUNT, MAXIMUM_AMOUNT } from '@/constants';
import { type InfoSheetKey } from '@/content/info-sheets';
import {
  useAmount,
  useChildFirstName,
  usePrivateAmount,
  useSetPrivateAmount,
} from '@/store';
import { track } from '@/analytics';
import { validateAmount } from '@/utils/helpers/amount.helpers';
import {
  GROWTH_RATES,
  growthRateLabel,
  projectAnnualGifts,
} from '@/utils/helpers/projection.helpers';

type AvatarProps = {
  firstName?: string;
  photoUrl?: string;
  showPhoto?: boolean;
  size?: number;
  borderPx?: number;
  initialFontSize?: number;
};

export function Avatar({
  firstName,
  photoUrl,
  showPhoto,
  size = 84,
  borderPx = 4,
  initialFontSize = 36,
}: AvatarProps) {
  const initial = (firstName || '?').charAt(0).toUpperCase();

  /**
   * Which URL failed to load, if any.
   *
   * Keyed on the URL rather than a plain boolean so a replacement gets its own
   * chance — the photo is a signed URL that expires, and when
   * `useGiftPhotoRefresh` swaps in a fresh signature it must not inherit the
   * dead one's failure.
   */
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  const hasPhoto = Boolean(showPhoto && photoUrl && photoUrl !== failedUrl);
  const common: React.CSSProperties = {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: '50%',
    border: `${borderPx}px solid #fff`,
    boxShadow: '0 10px 26px -14px rgba(20,40,32,0.45)',
    overflow: 'hidden',
  };
  if (hasPhoto) {
    return (
      <div style={common} role='img' aria-label={firstName ?? ''}>
        <img
          src={photoUrl}
          alt=''
          onError={() => setFailedUrl(photoUrl ?? null)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
    );
  }
  return (
    <div
      style={{
        ...common,
        background: 'linear-gradient(150deg,#66bda4,#256956)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      aria-hidden='true'
    >
      <span
        style={{ fontSize: initialFontSize, fontWeight: 700, color: '#fff' }}
      >
        {initial}
      </span>
    </div>
  );
}

type Variant = 'desktop' | 'mobile';

function tileButtonStyle(variant: Variant): React.CSSProperties {
  const isDesktop = variant === 'desktop';
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: isDesktop ? 6 : 9,
    flex: 1,
    height: isDesktop ? 62 : 88,
    background: '#fff',
    border: `1px solid ${isDesktop ? '#eae6dc' : '#eceae3'}`,
    borderRadius: isDesktop ? 13 : 16,
    padding: isDesktop ? '8px 10px' : '10px 12px',
    cursor: 'pointer',
    boxShadow: '0 6px 18px -14px rgba(20,40,32,0.3)',
  };
}

function tileLabelStyle(variant: Variant): React.CSSProperties {
  const isDesktop = variant === 'desktop';
  return {
    fontSize: isDesktop ? 11 : 12.5,
    fontWeight: 700,
    color: '#1a201d',
    textAlign: 'center',
    lineHeight: isDesktop ? 1.2 : 1.25,
  };
}

function tileIconSize(variant: Variant): number {
  return variant === 'desktop' ? 17 : 22;
}

const IconHow = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    stroke='#256956'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2v.3h6v-.3c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z' />
  </svg>
);

const IconCare = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    stroke='#256956'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M20.8 5.1a5.4 5.4 0 0 0-7.7 0L12 6.2l-1.1-1.1a5.4 5.4 0 0 0-7.7 7.7L12 21.5l8.8-8.7a5.4 5.4 0 0 0 0-7.7z' />
  </svg>
);

/**
 * The trust row on the amount step (GIFTWEB-32 AC 6).
 *
 * Two tiles, each opening an information sheet. A third, "Get support", stood
 * here until GIFTWEB-39 removed it; support is still reachable from the Need
 * help pill on every screen and from the allowance sheet, so nothing was lost
 * but the duplication.
 */
export function TrustTiles({ variant = 'desktop' }: { variant?: Variant }) {
  const bs = tileButtonStyle(variant);
  const ls = tileLabelStyle(variant);
  const is = tileIconSize(variant);

  const childFirstName = useChildFirstName();
  const [sheet, setSheet] = useState<InfoSheetKey | null>(null);

  const openSheet = (key: InfoSheetKey) => {
    track('infoSheetOpened', { sheet: key });
    setSheet(key);
  };

  return (
    <div style={{ display: 'flex', gap: variant === 'desktop' ? 8 : 9 }}>
      <button
        type='button'
        style={bs}
        aria-haspopup='dialog'
        onClick={() => openSheet('how')}
      >
        <IconHow size={is} />
        <span style={ls}>How does Mia work?</span>
      </button>
      <button
        type='button'
        style={bs}
        aria-haspopup='dialog'
        onClick={() => openSheet('protected')}
      >
        <IconCare size={is} />
        <span style={ls}>Handled with care</span>
      </button>
      <InfoSheet
        sheet={sheet}
        childFirstName={childFirstName}
        onOpenChange={(open) => !open && setSheet(null)}
      />
    </div>
  );
}

export function GrowthCard({ childName }: { childName?: string }) {
  const amount = useAmount();
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);

  // AC: the panel is hidden entirely when the amount is outside the valid range
  if (!validateAmount(amount, MINIMUM_AMOUNT, MAXIMUM_AMOUNT).valid) {
    return null;
  }

  const amountLabel = `£${amount.toLocaleString('en-GB')}`;
  const figure = (rate: number) =>
    `£${projectAnnualGifts(amount, rate).toLocaleString('en-GB')}`;

  return (
    <div
      className='relative overflow-hidden bg-[#eef3ef] border border-[#d9e6df] rounded-[18px] p-4.5 lg:p-5.5 text-left shadow-[0_10px_26px_-20px_rgba(15,41,34,0.4)]'
      data-testid='growth-card'
    >
      <div
        className='hidden lg:block text-xs font-bold tracking-wide text-[#256956] uppercase'
      >
        The power of compound growth
      </div>
      <div
        className='text-sm text-[#2f6b58] leading-normal mt-1.75'
      >
        {amountLabel} gifted each birthday could grow to
      </div>
      <div
        className='flex items-end gap-3 lg:gap-4 mt-3.5 tabular-nums'
      >
        <div className='flex-1 text-center'>
          <div
            className='text-[9.5px] font-bold tracking-wide text-[#6a8b7f] uppercase mb-1'
          >
            Low
          </div>
          <div
            className='text-[20px] font-bold tracking-[-0.02em] text-[#1e5546] leading-none'
            data-testid='projection-low'
          >
            {figure(GROWTH_RATES.low)}
          </div>
        </div>
        <div className='flex-[1.15] text-center px-0.5'>
          <div
            className='text-[9.5px] font-bold tracking-wide text-[#256956] uppercase mb-1'
          >
            Medium
          </div>
          <div
            className='text-[32px] font-bold tracking-[-0.02em] text-[#0f2922] leading-none'
            data-testid='projection-medium'
          >
            {figure(GROWTH_RATES.medium)}
          </div>
        </div>
        <div className='flex-1 text-center'>
          <div
            className='text-[9.5px] font-bold tracking-wide text-[#6a8b7f] uppercase mb-1'
          >
            High
          </div>
          <div
            className='text-[20px] font-bold tracking-[-0.02em] text-[#1e5546] leading-none'
            data-testid='projection-high'
          >
            {figure(GROWTH_RATES.high)}
          </div>
        </div>
      </div>
      <div
        className='text-[12.5px] text-[#3f7d6b] text-center mt-2.75'
      >
        by their 18th birthday
      </div>
      <div
        className='text-[10px] text-[#6a8b7f] leading-normal mt-2.75'
      >
        Illustration only, not a forecast.{' '}
        {/* GIFTWEB-39 item 5 — the warning names whose capital it is. */}
        <strong className='font-bold text-[#1e5546]'>
          Child's capital at risk.
        </strong>
        <button
          type='button'
          onClick={() => setAssumptionsOpen((open: boolean) => !open)}
          aria-expanded={assumptionsOpen}
          className='mt-1 flex items-center gap-0.5 bg-transparent border-none p-0 font-inherit text-[10px] font-bold text-[#256956] cursor-pointer underline'
        >
          See our assumptions
          <svg
            width='10'
            height='10'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.6'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
            className={cn(
              'transition-transform duration-200',
              assumptionsOpen && 'rotate-180',
            )}
          >
            <path d='m6 9 6 6 6-6' />
          </svg>
        </button>
      </div>
      {assumptionsOpen && (
        <div
          className='text-[10px] text-[#6a8b7f] leading-relaxed mt-2 px-3 py-2.5 bg-white border border-[#d8e7df] rounded-[10px]'
          data-testid='growth-assumptions'
        >
          A simple compound-growth projection, a guide, not a promise. It{' '}
          assumes {amountLabel} gifted on each of{' '}
          {childName ? `${childName}'s` : 'their'} birthdays, invested
          until they turn 18, growing at {growthRateLabel(GROWTH_RATES.low)} (low),{' '}
          {growthRateLabel(GROWTH_RATES.medium)} (medium) or{' '}
          {growthRateLabel(GROWTH_RATES.high)} (high) a year,
          every year, compounded monthly with returns reinvested. The figure doesn't account{' '}
          for inflation, fees or tax. Figures are rounded and for illustration only.
        </div>
      )}
    </div>
  );
}

export function PartnerBar({ variant = 'desktop' }: { variant?: Variant }) {
  const isDesktop = variant === 'desktop';
  const wrapperStyle: React.CSSProperties = isDesktop
    ? {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 18,
        paddingTop: 16,
        borderTop: '1px solid #e2ded4',
      }
    : {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 14,
        marginTop: 20,
      };
  const fidelityStyle: React.CSSProperties = isDesktop
    ? { height: 15, width: 'auto', maxWidth: 78, objectFit: 'contain' }
    : { height: 19, width: 'auto', maxWidth: 96, objectFit: 'contain' };
  const secclStyle: React.CSSProperties = isDesktop
    ? { height: 17, width: 'auto', maxWidth: 68, objectFit: 'contain' }
    : { height: 21, width: 'auto', maxWidth: 84, objectFit: 'contain' };
  const avivaStyle: React.CSSProperties = isDesktop
    ? { height: 12, width: 'auto', maxWidth: 56, objectFit: 'contain' }
    : { height: 15, width: 'auto', maxWidth: 70, objectFit: 'contain' };
  const dividerStyle: React.CSSProperties = isDesktop
    ? { width: 1, height: 13, background: '#d8d4ca' }
    : { width: 1, height: 16, background: '#e0ddd5' };
  return (
    <div style={wrapperStyle}>
      <img src={fidelityLogo} alt='Fidelity International' style={fidelityStyle} />
      <span style={dividerStyle} aria-hidden='true' />
      <img src={secclLogo} alt='Seccl' style={secclStyle} />
      <span style={dividerStyle} aria-hidden='true' />
      <img src={avivaLogo} alt='Aviva' style={avivaStyle} />
    </div>
  );
}

export function Copyright({ marginTop = 12 }: { marginTop?: number }) {
  return (
    <div
      style={{
        textAlign: 'center',
        fontSize: 10.5,
        color: '#a4a3a1',
        marginTop,
      }}
    >
      © 2026 Mia Wealth Ltd. All rights reserved.
    </div>
  );
}

type MobileGiftBoxProps = {
  firstName?: string;
  photoUrl?: string;
  showPhoto?: boolean;
  inviterName?: string;
};

export function MobileGiftBox({
  firstName,
  photoUrl,
  showPhoto,
  inviterName,
}: MobileGiftBoxProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        background: '#f1efe9',
        borderRadius: 18,
        padding: '14px 16px',
      }}
    >
      <Avatar
        firstName={firstName}
        photoUrl={photoUrl}
        showPhoto={showPhoto}
        size={62}
        borderPx={3}
        initialFontSize={27}
      />
      <div className='min-w-0 flex flex-col justify-start text-left'>
        <div
          style={{
            fontSize: 19,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: '#1a201d',
            lineHeight: 1.2,
          }}
        >
          A gift for {firstName ? `${firstName}'s` : 'their'} future
        </div>
        <div
          style={{
            fontSize: 13,
            color: '#5a635e',
            lineHeight: 1.45,
            marginTop: 4,
          }}
        >
          {inviterName ? (
            <>
              <strong style={{ color: '#1a201d' }}>{inviterName}</strong> has
              invited you to be part of it.
            </>
          ) : (
            <>You've been invited to be part of it.</>
          )}
        </div>
      </div>
    </div>
  );
}

type GiftSummaryChipProps = {
  firstName?: string;
  photoUrl?: string;
  showPhoto?: boolean;
  amountLabel: string;
};

/**
 * Compact "basket" header used from the details step onwards — avatar,
 * "Your gift to {child}'s future", amount pill (OTC prototype, step 3).
 */
export function GiftSummaryChip({
  firstName,
  photoUrl,
  showPhoto,
  amountLabel,
}: GiftSummaryChipProps) {
  return (
    <div
      className='flex items-center gap-[13px] rounded-2xl border border-[#d8e7df] bg-[#eef4f0] px-[15px] py-[13px] text-left'
      data-testid='gift-summary-chip'
    >
      <Avatar
        firstName={firstName}
        photoUrl={photoUrl}
        showPhoto={showPhoto}
        size={48}
        borderPx={2.5}
        initialFontSize={21}
      />
      <div className='min-w-0 flex-1'>
        <div className='text-[11px] text-[#5a7168]'>Your gift to</div>
        <div className='truncate text-base leading-tight font-bold tracking-[-0.01em] text-[#0f2922]'>
          {firstName ? `${firstName}'s` : 'their'} future
        </div>
      </div>
      <div className='inline-flex shrink-0 items-center rounded-[20px] border border-[#d2ebe1] bg-white px-[13px] py-1.5'>
        <span className='text-sm font-bold text-[#256956]'>{amountLabel}</span>
      </div>
    </div>
  );
}

export function MobileSignatureBar() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '6px 0 14px',
      }}
    >
      <MiaSignature height={30} color='#1a201d' />
    </div>
  );
}

export function PrivateToggle() {
  const privateAmount = usePrivateAmount();
  const setPrivateAmount = useSetPrivateAmount();

  return (
    <div className='flex items-start gap-3 rounded-2xl border-[1.5px] border-[#e8e4de] bg-white px-[15px] py-3.5 text-left'>
      <div className='min-w-0 flex-1'>
        <div className='text-[14.5px] font-bold text-[#201f1d]'>
          Keep my amount private
        </div>
        <div className='mt-[3px] text-[12.5px] leading-[1.45] text-[#72706f]'>
          They'll see your gift, but not the amount.
        </div>
      </div>
      <button
        type='button'
        role='switch'
        aria-checked={privateAmount}
        aria-label='Keep my amount private'
        onClick={() => setPrivateAmount(!privateAmount)}
        data-testid='private-toggle'
        className={cn(
          'flex h-[31px] w-[52px] shrink-0 cursor-pointer items-center rounded-2xl border-none p-[3px] transition-colors duration-200',
          privateAmount ? 'justify-end bg-[#256956]' : 'justify-start bg-[#d2d2d0]',
        )}
      >
        <span className='block size-[25px] rounded-full bg-white shadow-[0_2px_5px_rgba(15,41,34,0.28)]' />
      </button>
    </div>
  );
}

function LandingContext({
  firstName,
  photoUrl,
  showPhoto,
  inviterName,
}: MobileGiftBoxProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <MiaSignature height={30} color='#1a201d' />
      <div
        style={{
          fontSize: 31,
          fontWeight: 700,
          letterSpacing: '-0.025em',
          color: '#1a201d',
          lineHeight: 1.15,
          marginTop: 22,
          maxWidth: 360,
        }}
      >
        A gift for {firstName ? `${firstName}'s` : 'their'} future
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginTop: 20,
        }}
      >
        <Avatar
          firstName={firstName}
          photoUrl={photoUrl}
          showPhoto={showPhoto}
        />
        <div style={{ fontSize: 15, color: '#5a635e', lineHeight: 1.55 }}>
          {inviterName ? (
            <>
              <strong style={{ color: '#1a201d' }}>{inviterName}</strong> has
              invited you to be part of{' '}
              <strong style={{ color: '#1a201d' }}>
                {firstName ? `${firstName}'s` : 'their'}
              </strong>{' '}
              future.
            </>
          ) : (
            <>
              You've been invited to be part of{' '}
              <strong style={{ color: '#1a201d' }}>
                {firstName ? `${firstName}'s` : 'their'}
              </strong>{' '}
              future.
            </>
          )}
        </div>
      </div>
      <div style={{ marginTop: 24 }}>
        <GrowthCard childName={firstName} />
      </div>
      <div style={{ marginTop: 28 }}>
        <TrustTiles variant='desktop' />
      </div>
      <PartnerBar variant='desktop' />
      <Copyright marginTop={12} />
    </div>
  );
}

export default LandingContext;
